import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { UserService } from '../../../core/auth/user.service';
import { TENANT_DISPLAY_NAMES, TENANT_SLUGS } from '../../constants/tenants';

/**
 * Per-tenant logo descriptor.
 * - `icon`: a square app-icon mark (e.g. the Cover cube) carrying its own
 *   background, drawn as a small tile.
 * - `wordmark`: a wide single-colour brand wordmark (e.g. Tryba) that would be
 *   illegible directly on the blue gradient header, so it is laid on a white
 *   plate that stays readable on every surface.
 */
interface TenantLogo {
  src: string;
  kind: 'icon' | 'wordmark';
}

/**
 * Tenant scope marker: the logged-in company's logo, no company name (the logo
 * speaks for itself and the user's name follows it in the header). Cover shows
 * the Cover cube, Tryba shows the Tryba wordmark. A tenant without a logo asset
 * falls back to a monogram. Pass `[showLabel]="true"` to render the company
 * name next to the logo where there is no adjacent user context.
 */
@Component({
  selector: 'app-tenant-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tenant-chip.component.html',
  styleUrls: ['./tenant-chip.component.scss'],
})
export class TenantChipComponent {
  private readonly userService = inject(UserService);

  readonly tenantId = input<string | undefined>(undefined);
  readonly showLabel = input<boolean>(false);

  private static readonly LOGO_BY_TENANT: Record<string, TenantLogo> = {
    // Relative (no leading slash) so they resolve against the deployed
    // <base href="/en/"> — root-absolute paths 404 under the i18n subdir.
    [TENANT_SLUGS.COVER3D_DIRECT]: { src: 'Cover-Logo-512x512-blue.png', kind: 'icon' },
    [TENANT_SLUGS.TRYBA]: { src: 'tryba-logo.svg', kind: 'wordmark' },
  };

  protected readonly displayedTenantId = computed<string>(
    () => this.tenantId() ?? this.userService.tenantId(),
  );

  protected readonly tenantLabel = computed<string>(() => {
    const id = this.displayedTenantId();
    if (!id) return TENANT_DISPLAY_NAMES[TENANT_SLUGS.COVER3D_DIRECT];
    return TENANT_DISPLAY_NAMES[id] ?? id;
  });

  protected readonly logo = computed<TenantLogo | null>(() => {
    const id = this.displayedTenantId() || TENANT_SLUGS.COVER3D_DIRECT;
    return TenantChipComponent.LOGO_BY_TENANT[id] ?? null;
  });

  protected readonly monogram = computed<string>(() => (this.tenantLabel()[0] ?? '?').toUpperCase());
}
